// The WHOLE requested-documents checklist, read once — shared by the admin's and
// the seller's checklist pages (checklist redesign slice 1; parallel paging and
// single-item cache patching from e0a33fb7). No hooks here: each app wraps these
// in its own react-query hooks. The server decides what the caller can read — a
// seller's read is release-gated (released phases only), an admin's is not.
import type { QueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { DdRequest, ListDdRequestsResponse } from './types';

/** The list endpoint's largest page (`DD_REQUESTS_MAX_LIMIT`, src/modules/dd). */
export const DD_PAGE_MAX = 200;

/** How long the ADMIN's whole checklist is fresh. A person's own single-item
 * writes patch it in place ({@link patchDdRequestInCache}) and structural ones
 * invalidate it, so this only bounds how late the AI's background matches show
 * up on a re-visit. The seller's list uses the seller app's default instead, so
 * it refreshes together with the counts tiles above it. */
export const ALL_DD_STALE_MS = 5 * 60_000;

/** The whole-checklist cache key. It shares the `dd-requests` prefix, so a
 * structural DD invalidation still refetches it. */
export const allDdRequestsKey = (roomId: string) =>
  ['rooms', roomId, 'dd-requests', 'all'] as const;

/** Every item the caller can read, in the server's order: page 1 says `total`,
 * then the remaining pages are read in parallel (3 calls for the ~460-item
 * default). The pages are separate reads, so a write landing between them can
 * shift an item across a page boundary: items are de-duplicated by id, and if a
 * later page reports a different `total` than page 1 the whole read runs once
 * more. */
export async function fetchAllDdRequests(roomId: string): Promise<DdRequest[]> {
  const page = (offset: number) =>
    api.get<ListDdRequestsResponse>(
      `/rooms/${roomId}/dd-requests?limit=${DD_PAGE_MAX}&offset=${offset}`,
    );
  const readAll = async (): Promise<{ requests: DdRequest[]; consistent: boolean }> => {
    const first = await page(0);
    if (first.requests.length === 0 || first.requests.length >= first.total) {
      return { requests: first.requests, consistent: true };
    }
    const offsets: number[] = [];
    for (let offset = DD_PAGE_MAX; offset < first.total; offset += DD_PAGE_MAX) {
      offsets.push(offset);
    }
    const rest = await Promise.all(offsets.map(page));
    return {
      requests: [first, ...rest].flatMap((p) => p.requests),
      consistent: rest.every((p) => p.total === first.total),
    };
  };
  let read = await readAll();
  if (!read.consistent) read = await readAll();
  const byId = new Map<string, DdRequest>();
  for (const r of read.requests) if (!byId.has(r.id)) byId.set(r.id, r);
  return [...byId.values()];
}

/**
 * After a SINGLE-ITEM write that returns the updated DdRequest: patch that item
 * into the whole-checklist cache instead of refetching every page of it. The
 * counts come from the summary endpoint, so it still refetches (one small
 * aggregate), as do any paged slices and the section certifications the write
 * may have re-opened. Structural changes keep a full invalidation.
 */
export function patchDdRequestInCache(qc: QueryClient, roomId: string, updated: DdRequest): void {
  // A read already in flight may have read the row before this write and would
  // overwrite the patch when it lands — so it is restarted instead.
  const inFlight = qc.isFetching({ queryKey: allDdRequestsKey(roomId) }) > 0;
  qc.setQueryData<DdRequest[]>(allDdRequestsKey(roomId), (old) =>
    old?.map((r) => (r.id === updated.id ? updated : r)),
  );
  void qc.invalidateQueries({
    queryKey: ['rooms', roomId, 'dd-requests'],
    predicate: (q) => inFlight || q.queryKey[3] !== 'all',
  });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'dd-summary'] });
  void qc.invalidateQueries({ queryKey: ['rooms', roomId, 'certifications'] });
}

/** Whether another cached item is waiting on (has an AI suggestion of) this
 * document. Linking it settles that document's suggestions on EVERY item, so the
 * whole checklist must refetch rather than patch one item. */
export function otherItemsWaitingOn(
  qc: QueryClient,
  roomId: string,
  requestId: string,
  documentId: string,
): boolean {
  return (
    qc
      .getQueryData<DdRequest[]>(allDdRequestsKey(roomId))
      ?.some((r) => r.id !== requestId && (r.reviewDocumentIds ?? []).includes(documentId)) ??
    false
  );
}
